import React, { createContext, useState, useEffect } from "react";
import { signOut, getCurrentUser, signInWithRedirect } from "@aws-amplify/auth";
import { Hub } from "@aws-amplify/core";

import { StandardContextProps } from "@/types/contextTypes";
import {
  DeleteShipResponse,
  ShipDataType,
  ShipFormValues,
  StudyFormValues,
  UserContextType,
} from "@/types/userContextTypes";
import { useCreateShip } from "@/hooks/apiHooks/useCreateShip";
import { useCloneUserShip } from "@/hooks/apiHooks/useCloneUserShip";
import { useDeleteShip } from "@/hooks/apiHooks/useDeleteShip";
import { useFetchUserShips } from "@/hooks/apiHooks/useFetchUserShips";
import { useAssociateShipToProject } from "@/hooks/apiHooks/useAssociateShipToProject";
import { ShipCreatePayload } from "@/types/apiTypes";

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserProvider: React.FC<StandardContextProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [formValues, setFormValues] = useState<ShipFormValues>({});
  const [studyFormValues, setStudyFormValues] = useState<StudyFormValues>({});

  const { mutateAsync: createShipMutation } = useCreateShip();
  const { mutateAsync: cloneShipMutation } = useCloneUserShip();
  const { deleteShip } = useDeleteShip();
  const { mutateAsync: associateShipToProject } = useAssociateShipToProject();
  const { data: ships = [] } = useFetchUserShips();

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      console.log("User session restored:", currentUser);
    } catch {
      setUser(null);
      console.log("No existing user session");
    }
  };

  useEffect(() => {
    checkUser();
    const listener = (data: any) => {
      switch (data.payload.event) {
        case "signIn":
          checkUser();
          break;
        case "signOut":
          setUser(null);
          break;
        default:
          break;
      }
    };

    const hubListener = Hub.listen("auth", listener);

    return () => {
      hubListener();
    };
  }, []);

  const login = async () => {
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const saveShipData = async (
    data: Omit<ShipCreatePayload, "id" | "user_id"> & { projectId?: string },
    projectIdParam?: string,
  ) => {
    if (!user) throw new Error("User is not authenticated");

    try {
      const projectId = projectIdParam ?? data.projectId;
      const { projectId: _discard, ...payload } = data;

      const savedShip = await createShipMutation(payload);

      if (projectId) {
        await associateShipToProject({ ship: savedShip, projectId: projectId });
      }
    } catch (error) {
      console.error("Error saving ship data:", error);
    }
  };

  const removeShip = async (shipId: string): Promise<DeleteShipResponse> => {
    if (!user)
      return { status: "failure", message: "User is not authenticated" };

    try {
      return await deleteShip(shipId);
    } catch (error) {
      console.error("Error deleting ship:", error);
      throw error;
    }
  };

  const cloneShip = async (shipId: string) => {
    if (!user) {
      throw new Error("User is not authenticated");
    }
    try {
      await cloneShipMutation(shipId);
    } catch (error) {
      console.error("Error cloning ship:", error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        formValues,
        studyFormValues,
        ships,
        setUser,
        setFormValues,
        setStudyFormValues,
        login,
        logout,
        saveShipData,
        deleteShip: removeShip,
        cloneShip,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export type { UserContextType };
